import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface SubscribeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SubscribeModal = ({ open, onOpenChange }: SubscribeModalProps) => {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribing(true);
    const { error } = await supabase.from("newsletter_subscribers").insert({
      email: email.trim(),
      source: "header_modal",
    });
    setSubscribing(false);
    if (error) {
      if (error.code === "23505") {
        toast.info("You're already subscribed!");
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
      return;
    }
    setSuccess(true);
    toast.success("Subscribed! Welcome to ForexWireDaily Insider.");
  };

  const handleClose = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      // Reset state when modal closes
      setTimeout(() => {
        setEmail("");
        setSuccess(false);
      }, 200);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md glass border-primary/20">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {success ? "You're In!" : "Stay Ahead of the Wire"}
          </DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="h-14 w-14 rounded-full bg-primary/20 flex items-center justify-center">
              <Check className="h-7 w-7 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Welcome to ForexWireDaily Insider. You'll get breaking news, exposés, and market alerts straight to your inbox.
            </p>
            <Button size="sm" variant="outline" onClick={() => handleClose(false)}>
              Close
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Get breaking forex & fintech news, prop firm exposés, and regulatory alerts delivered to your inbox.
            </p>
            <form onSubmit={handleSubscribe} className="flex items-center gap-2">
              <Input
                type="email"
                placeholder="your@email.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-10 bg-muted/50 border-border"
              />
              <Button size="default" className="shrink-0" disabled={subscribing}>
                <Mail className="h-4 w-4 mr-1.5" />
                {subscribing ? "..." : "Subscribe"}
              </Button>
            </form>
            <p className="text-[10px] text-muted-foreground/60">
              No spam. Unsubscribe anytime. We respect your privacy.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SubscribeModal;
